import { forwardRef, useState, type HTMLAttributes, type ReactNode } from "react";

import { Input } from "@/components/ds";

// A single calendar date. From d2d-UEM ds-uem/, not in d2d-XDR ds/ (d2d-UEM plan 3.1): @datasirpi/ui had it
// and SIEM's ds/ does not. Props are the vendor's, name for name.
//
// A native <input type="date"> inside ds/Input, so it shares the field's
// label, help, error and focus treatment, and the browser supplies an
// accessible, localised picker. Values are ISO dates (YYYY-MM-DD), as before;
// an empty field reports "". For a time WINDOW use ds/TimeRangePicker.

export type ISODate = string;

export interface DatePickerRules {
  required?: boolean | string;
  min?: string;
  max?: string;
}

export interface DatePickerProps extends Omit<HTMLAttributes<HTMLInputElement>, "onChange" | "defaultValue" | "value"> {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  value?: ISODate;
  defaultValue?: ISODate;
  onChange?: (value: ISODate) => void;
  min?: string;
  max?: string;
  rules?: DatePickerRules;
  validateOn?: "blur" | "change" | "submit";
  onValidate?: (error: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

function validate(value: string, rules: DatePickerRules | undefined, required: boolean | undefined): string | null {
  const req = rules?.required ?? required;
  if (req && !value) return typeof req === "string" ? req : "Choose a date.";
  const min = rules?.min;
  const max = rules?.max;
  if (value && min && value < min) return `Choose ${min} or later.`;
  if (value && max && value > max) return `Choose ${max} or earlier.`;
  return null;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { label, help, error, required, value, defaultValue, onChange, min, max, rules, validateOn = "blur", onValidate, onBlur, ...rest },
  ref
) {
  const [ownError, setOwnError] = useState<string | null>(null);
  const run = (v: string) => {
    if (!rules && !required) return;
    const next = validate(v, rules, required);
    setOwnError(next);
    onValidate?.(next);
  };

  return (
    <Input
      ref={ref}
      type="date"
      label={label}
      help={help}
      error={error ?? ownError ?? undefined}
      required={required}
      value={value}
      defaultValue={defaultValue}
      min={min ?? rules?.min}
      max={max ?? rules?.max}
      onChange={(e) => {
        onChange?.(e.target.value);
        if (validateOn === "change") run(e.target.value);
      }}
      onBlur={(e) => {
        onBlur?.(e);
        if (validateOn === "blur") run(e.target.value);
      }}
      {...rest}
    />
  );
});
